import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';

import CloudIcon from '../../svgs/CloudIcon';
import { LogManager } from '../../utility/Logger';

import type { DropEvent, DropzoneState, FileRejection } from 'react-dropzone';

type Props = {
  width?: number;
  height?: number;
  callback: (file: File[]) => void;
  acceptType?: 'all' | 'image' | 'video';
  multiple?: boolean;
  description?: React.ReactNode;
  iconJSX?: React.ReactNode;
  videoFormats?: ('video/mp4' | 'video/mov' | 'video/quicktime')[];
  inputTestId?: string;
};

const VIDEO_FORMAT_EXTENSIONS: Record<string, string[]> = {
  'video/mp4': ['.mp4'],
  // `video/mov` is not a real MIME type; kept for existing callers. Real
  // QuickTime files report `video/quicktime` — prefer it for the story path.
  'video/mov': ['.mov'],
  'video/quicktime': ['.mov', '.qt'],
};

const IMAGE_FORMAT_EXTENSIONS = {
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/jpg': ['.jpg', '.jpeg'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
  'image/webp': ['.webp'],
};

const IMAGE_FORMATS = [...Object.keys(IMAGE_FORMAT_EXTENSIONS), 'image/jpeg'];

const DragAndDrop = ({
  width = 350,
  height = 137,
  callback,
  acceptType = 'all',
  multiple = true,
  description = 'Drag and drop your files here',
  iconJSX = (
    <Icon>
      <CloudIcon width="20" height="20" />
    </Icon>
  ),
  videoFormats = ['video/mp4'],
  inputTestId,
}: Props) => {
  const logger = LogManager.getLogger('component:DragAndDrop');
  const isMobile = window.innerWidth <= 615;

  const formats: string[] = {
    all: [...IMAGE_FORMATS, ...videoFormats],
    image: IMAGE_FORMATS,
    video: videoFormats,
  }[acceptType];

  const videoAccept = videoFormats.reduce((acc, key) => {
    return {
      ...acc,
      [key]: VIDEO_FORMAT_EXTENSIONS[key],
    };
  }, {});

  const { getRootProps, getInputProps }: DropzoneState = useDropzone({
    accept: {
      all: {
        ...IMAGE_FORMAT_EXTENSIONS,
        ...videoAccept,
      },
      image: IMAGE_FORMAT_EXTENSIONS,
      video: videoAccept,
    }[acceptType],
    multiple,
    onDragEnter: (event: React.DragEvent<HTMLElement>) => {
      logger.debug('dragEnter', event);
    },
    onError: (err: Error) => {
      logger.warn('error', err);
    },
    onDropRejected: (fileRejections: FileRejection[], event: DropEvent) => {
      logger.warn('fileRejected', fileRejections, event);
    },
    onDrop: useCallback<(_: File[]) => void>(
      acceptedFiles => {
        callback(acceptedFiles);
      },
      [callback],
    ),
  });

  //const RenderFile = (f: ExtendedFile, idx: number) => {
  //  const removeFile = () => {
  //    const filteredFiles = files.filter((file) => file.preview !== f.preview);
  //    const newFiles = filteredFiles.map((f) =>
  //      Object.assign(f, {
  //        preview: URL.createObjectURL(f),
  //      }),
  //    );
  //    setFiles(newFiles);
  //  };
  //  return (
  //    <Container filesCount={files.length} idx={idx}>
  //      <Actions className="actions">
  //        <Button type="button" onClick={removeFile}>
  //          <span>
  //            <RemoveIcon /> Remove
  //          </span>
  //        </Button>
  //      </Actions>
  //      {f.type !== 'video/mp4' ? (
  //        <Image
  //          src={f.preview}
  //          width={100}
  //          height={100}
  //          alt="Image"
  //          idx={idx}
  //          filesCount={files.length}
  //          onLoad={() => {
  //            URL.revokeObjectURL(f.preview);
  //          }}
  //        />
  //      ) : (
  //        <Video idx={idx} src={f.preview} filesCount={files.length} />
  //      )}
  //    </Container>
  //  );
  //};

  return (
    <DragZone
      className="container"
      width={width}
      height={height}
      {...(!isMobile && getRootProps({ className: 'dropzone' }))}
    >
      {isMobile ? (
        <File
          type="file"
          title=""
          multiple
          accept={formats.join(',')}
          data-testid={inputTestId}
          onChange={e => {
            const fileList = e.target.files;
            if (fileList) {
              const filesArray = Array.from<File>(fileList);
              callback(filesArray);
            }
          }}
        />
      ) : (
        <DragContent>
          <input {...getInputProps()} data-testid={inputTestId} />
          {iconJSX}
          <Text>{description}</Text>
        </DragContent>
      )}

      {isMobile && (
        <DragContent>
          {iconJSX}
          <Text>{description}</Text>
        </DragContent>
      )}
    </DragZone>
  );
};

export default DragAndDrop;

const DragZone = styled.section<{ width: number; height: number }>`
  display: flex;
  position: relative;
  background-color: var(--surface-elevated);
  border: 1px dashed var(--text-muted);
  border-radius: 8px;
  width: ${props => props.width}px;
  height: ${props => props.height}px;
  cursor: pointer;
  transition: all var(--transition-normal) ease;
  &:hover {
    background-color: var(--border);
    border-color: var(--accent);
  }
  @media only screen and (max-width: 615px) {
    width: 343px;
    height: 206px;
  }
`;

const File = styled.input`
  width: 100%;
  height: 100%;
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

const Icon = styled.div`
  margin: 0 auto;
  text-align: center;
  margin-bottom: 8px;
  svg {
    display: block;
  }
`;

const DragContent = styled.div`
  weight: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 auto;
  color: var(--text-secondary);
`;
const Text = styled.span`
  font-weight: var(--font-weight-regular);
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-family: var(--font-primary);
  line-height: 16.8px;
  text-align: center;
`;

//const Container = styled.div<{ filesCount: number; idx: number }>`
//  display: flex;
//  min-width: 0;
//  overflow: hidden;
//  width: calc(100% / ${(props) => Math.min(props.filesCount, 3)});
//  height: ${(props) => (props.filesCount > 3 ? 'calc(50%)' : '100%')};
//  position: relative;
//
//  ${({ filesCount, idx }) =>
//    filesCount > 2 &&
//    css`
//      border-top-left-radius: ${filesCount > 2 && idx === 0 && '0.5rem'};
//      border-top-right-radius: ${filesCount > 2 && idx === 2 && '0.5rem'};
//      border-bottom-left-radius: ${filesCount > 2 && idx === 3 && '0.5rem'};
//      border-bottom-right-radius: ${filesCount > 2 && idx === 5 && '0.5rem'};
//    `}
//  ${({ filesCount, idx }) =>
//    filesCount === 2 &&
//    css`
//      border-top-left-radius: ${idx === 0 && '0.5rem'};
//      border-bottom-left-radius: ${idx === 0 && '0.5rem'};
//      border-top-right-radius: ${idx === 1 && '0.5rem'};
//      border-bottom-right-radius: ${idx === 1 && '0.5rem'};
//    `}
//  ${({ filesCount, idx }) =>
//    filesCount === 1 &&
//    css`
//      border-radius: 0.5rem;
//    `}
//
//  &:hover > .actions {
//    opacity: 1;
//  }
//`;

//const Actions = styled.div`
//  position: absolute;
//  width: 100%;
//  height: 100%;
//  top: 0;
//  display: flex;
//  flex-direction: column;
//  justify-content: center;
//  gap: 5px;
//  opacity: 0;
//  transition: opacity 300ms;
//  z-index: 50;
//
//  &:hover {
//    background-color: rgba(209, 213, 219, 0.5);
//  }
//`;

//const Button = styled.button`
//  // width: 32px;
//  margin: auto;
//  border-radius: 8px;
//  font-size: 12px;
//  padding: 12px 16px;
//  display: flex;
//  align-items: center;
//  justify-content: center;
//  outline: 0;
//  border: 0;
//  background-color: #03041a;
//  color: #f3e9d7;
//  span {
//    display: flex;
//    align-items: center;
//    gap: 5px;
//    cursor: pointer;
//  }
//`;

//const Image = styled.img<{ idx: number; filesCount: number }>`
//  display: block;
//  ${({ filesCount, idx }) =>
//    filesCount > 2 &&
//    css`
//      border-top-left-radius: ${filesCount > 2 && idx === 0 && '0.5rem'};
//      border-top-right-radius: ${filesCount > 2 && idx === 2 && '0.5rem'};
//      border-bottom-left-radius: ${filesCount > 2 && idx === 3 && '0.5rem'};
//      border-bottom-right-radius: ${filesCount > 2 && idx === 5 && '0.5rem'};
//    `}
//  ${({ filesCount, idx }) =>
//    filesCount === 2 &&
//    css`
//      border-top-left-radius: ${idx === 0 && '0.5rem'};
//      border-bottom-left-radius: ${idx === 0 && '0.5rem'};
//      border-top-right-radius: ${idx === 1 && '0.5rem'};
//      border-bottom-right-radius: ${idx === 1 && '0.5rem'};
//    `}
//  ${({ filesCount, idx }) =>
//    filesCount === 1 &&
//    css`
//      border-radius: 0.5rem;
//    `}
//  object-fit: cover;
//  width: 100%;
//  height: 100%;
//  object-position: top;
//`;

//const Video = styled.video<{ idx: number; filesCount: number }>`
//  display: block;
//  ${({ filesCount, idx }) =>
//    filesCount > 2 &&
//    css`
//      border-top-left-radius: ${filesCount > 2 && idx === 0 && '0.5rem'};
//      border-top-right-radius: ${filesCount > 2 && idx === 2 && '0.5rem'};
//      border-bottom-left-radius: ${filesCount > 2 && idx === 3 && '0.5rem'};
//      border-bottom-right-radius: ${filesCount > 2 && idx === 5 && '0.5rem'};
//    `}
//  ${({ filesCount, idx }) =>
//    filesCount === 2 &&
//    css`
//      border-top-left-radius: ${idx === 0 && '0.5rem'};
//      border-bottom-left-radius: ${idx === 0 && '0.5rem'};
//      border-top-right-radius: ${idx === 1 && '0.5rem'};
//      border-bottom-right-radius: ${idx === 1 && '0.5rem'};
//    `}
//  ${({ filesCount, idx }) =>
//    filesCount === 1 &&
//    css`
//      border-radius: 0.5rem;
//    `}
//  object-fit: cover;
//  width: 100%;
//  height: 100%;
//  object-position: top;
//`;

//const DisplayFiles = styled.div`
//  display: flex;
//  width: 100%;
//  height: 100%;
//  flex-wrap: wrap;
//`;
//
//const MoreItems = styled.div`
//  color: #f3e9d7;
//  font-weight: 700;
//  display: flex;
//  align-items: center;
//  justify-content: center;
//  text-align: center;
//  margin: 0 auto;
//  font-size: 10px;
//`;
